var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/index.ts
import {
  getClientEnv as getCoreClientEnv,
  getEnv as getCoreEnv,
  getEnvArray as getCoreEnvArray,
  getEnvBoolean as getCoreEnvBoolean,
  getEnvJson as getCoreEnvJson,
  getEnvNumber as getCoreEnvNumber,
  getEnvString as getCoreEnvString,
  getEnvUrl as getCoreEnvUrl,
  hasEnv as hasCoreEnv
} from "./core.js";
import { getRuntimeInfo } from "./platform.js";
import { isClientEnvKey, validateEnvAccess } from "./validator.js";
import { env as validatedEnvironment } from "#validated-env";
import { CLIENT_ENV_PREFIX } from "./client-env.js";
import {
  getEnvironment,
  getEnvs,
  isDevelopment,
  isE2EBypassPermitted,
  isProduction,
  isStaging,
  isTest
} from "./core.js";
import {
  detectPlatform,
  detectRuntime,
  getApiUrl,
  getBaseUrl,
  getRuntimeInfo as getRuntimeInfo2,
  isAWS,
  isBrowserRuntime,
  isCloudflare,
  isEdgeRuntime,
  isLocal,
  isNodeRuntime,
  isVercel,
  isVercelProductionDeployment,
  resolveSelfInvokeBaseUrl
} from "./platform.js";
import { isClientEnvKey as isClientEnvKey2, isClientSide, isServerSide } from "./validator.js";
function getEnv(key, options = {}) {
  validateEnvAccess(key);
  return getCoreEnv(key, options);
}
__name(getEnv, "getEnv");
function getEnvString(key, defaultValue) {
  validateEnvAccess(key);
  return getCoreEnvString(key, defaultValue);
}
__name(getEnvString, "getEnvString");
function getClientEnv(key) {
  return getCoreClientEnv(key);
}
__name(getClientEnv, "getClientEnv");
function getEnvBoolean(key, defaultValue = false) {
  validateEnvAccess(key);
  return getCoreEnvBoolean(key, defaultValue);
}
__name(getEnvBoolean, "getEnvBoolean");
function getEnvNumber(key, defaultValue = 0) {
  validateEnvAccess(key);
  return getCoreEnvNumber(key, defaultValue);
}
__name(getEnvNumber, "getEnvNumber");
function getEnvUrl(key, defaultValue) {
  validateEnvAccess(key);
  return getCoreEnvUrl(key, defaultValue);
}
__name(getEnvUrl, "getEnvUrl");
function getEnvArray(key, defaultValue = []) {
  validateEnvAccess(key);
  return getCoreEnvArray(key, defaultValue);
}
__name(getEnvArray, "getEnvArray");
function getEnvJson(key, defaultValue) {
  validateEnvAccess(key);
  return getCoreEnvJson(key, defaultValue);
}
__name(getEnvJson, "getEnvJson");
function hasEnv(key) {
  try {
    validateEnvAccess(key);
    return hasCoreEnv(key);
  } catch {
    return false;
  }
}
__name(hasEnv, "hasEnv");
function getEnvOrThrow(key, options = {}) {
  return getEnv(key, { ...options, required: true });
}
__name(getEnvOrThrow, "getEnvOrThrow");
function getConfig() {
  const runtime = getRuntimeInfo();
  return {
    runtime,
    appUrl: getEnvUrl("NEXT_PUBLIC_APP_URL", "http://localhost:3030"),
    environment: getEnvString("NEXT_PUBLIC_ENVIRONMENT", "development"),
    features: {
      pwa: getEnvBoolean("NEXT_PUBLIC_ENABLE_PWA", false),
      devMode: getEnvBoolean("NEXT_PUBLIC_DEV_MODE", false),
      analytics: getEnvBoolean("NEXT_PUBLIC_ENABLE_ANALYTICS", false),
      monitoring: getEnvBoolean("NEXT_PUBLIC_ENABLE_MONITORING", false),
      aiFeatures: getEnvBoolean("NEXT_PUBLIC_ENABLE_AI_FEATURES", true),
      darkMode: getEnvString("NEXT_PUBLIC_THEME_DEFAULT", "system") !== "light"
    }
  };
}
__name(getConfig, "getConfig");
function createEnvGetter(envKeys) {
  const result = {};
  for (const [name, key] of Object.entries(envKeys)) {
    result[name] = getEnvString(key);
  }
  return result;
}
__name(createEnvGetter, "createEnvGetter");
function getKindeConnectionIds() {
  return {
    linkedin: getEnvString("NEXT_PUBLIC_KINDE_CONNECTION_LINKEDIN", ""),
    google: getEnvString("NEXT_PUBLIC_KINDE_CONNECTION_GOOGLE", ""),
    email: getEnvString("NEXT_PUBLIC_KINDE_CONNECTION_EMAIL", "")
  };
}
__name(getKindeConnectionIds, "getKindeConnectionIds");
function getKindeConnectionId(provider) {
  const connections = getKindeConnectionIds();
  return connections[provider];
}
__name(getKindeConnectionId, "getKindeConnectionId");
function getPusherConfig() {
  return {
    // Server configuration (for sending events)
    server: {
      appId: getEnvString("PUSHER_APP_ID", ""),
      key: getEnvString("PUSHER_KEY", ""),
      secret: getEnvString("PUSHER_SECRET", ""),
      cluster: getEnvString("PUSHER_CLUSTER", "us2")
    },
    // Client configuration (for receiving events)
    client: {
      key: getEnvString("NEXT_PUBLIC_PUSHER_KEY", ""),
      cluster: getEnvString("NEXT_PUBLIC_PUSHER_CLUSTER", "us2")
    }
  };
}
__name(getPusherConfig, "getPusherConfig");
function debugEnvironment() {
  const runtime = getRuntimeInfo();
  const availableVars = Object.keys(validatedEnvironment).sort();
  const publicVars = [];
  const serverVars = [];
  for (const key of availableVars) {
    if (isClientEnvKey(key)) {
      publicVars.push(key);
    } else {
      serverVars.push(key);
    }
  }
  return {
    runtime,
    publicVars,
    serverVars,
    availableVars,
    config: getConfig()
  };
}
__name(debugEnvironment, "debugEnvironment");
var env = validatedEnvironment;
function setEnv(key, value) {
  if (typeof process !== "undefined" && process.env) {
    process.env[key] = value;
  }
}
__name(setEnv, "setEnv");
function deleteEnv(key) {
  if (typeof process !== "undefined" && process.env) {
    delete process.env[key];
  }
}
__name(deleteEnv, "deleteEnv");
export {
  CLIENT_ENV_PREFIX,
  createEnvGetter,
  debugEnvironment,
  deleteEnv,
  detectPlatform,
  detectRuntime,
  env,
  getApiUrl,
  getBaseUrl,
  getClientEnv,
  getConfig,
  getEnv,
  getEnvArray,
  getEnvBoolean,
  getEnvJson,
  getEnvNumber,
  getEnvOrThrow,
  getEnvString,
  getEnvUrl,
  getEnvironment,
  getEnvs,
  getKindeConnectionId,
  getKindeConnectionIds,
  getPusherConfig,
  getRuntimeInfo2 as getRuntimeInfo,
  hasEnv,
  isAWS,
  isBrowserRuntime,
  isClientEnvKey2 as isClientEnvKey,
  isClientSide,
  isCloudflare,
  isDevelopment,
  isE2EBypassPermitted,
  isEdgeRuntime,
  isLocal,
  isNodeRuntime,
  isProduction,
  isServerSide,
  isStaging,
  isTest,
  isVercel,
  isVercelProductionDeployment,
  resolveSelfInvokeBaseUrl,
  setEnv
};
